import { MetricoolOrganicPanel } from '../components/MetricoolOrganicPanel';
import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft, Download, Share2, Loader2, Sparkles, Save, CheckCircle2,
  Link2, Copy, BarChart3, FileText, Eye, AlertTriangle, Pencil,
  Facebook, Instagram, Linkedin, Youtube
} from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import { ReportChart } from '../components/DashboardChart';
import MetricCard from '../components/MetricCard';

// Platform icons and colors
const PLATFORM_META = {
  facebook: { color: '#1877F2', label: 'Facebook', letter: 'f' },
  instagram: { color: '#E4405F', label: 'Instagram', letter: 'i' },
  linkedin: { color: '#0A66C2', label: 'LinkedIn', letter: 'in' },
  tiktok: { color: '#000000', label: 'TikTok', letter: 't' },
  youtube: { color: '#FF0000', label: 'YouTube', letter: 'y' },
};

function PlatformIcon({ platform, size = 18 }) {
  const meta = PLATFORM_META[platform] || {};
  return (
    <div
      className="rounded-md flex items-center justify-center text-white font-bold flex-shrink-0"
      style={{ background: meta.color || '#666', width: size, height: size, fontSize: size * 0.45 }}
    >
      {meta.letter?.toUpperCase() || '?'}
    </div>
  );
}

// Editable list item for highlights/opportunities
function EditableListItem({ value, index, color, isFinalized, onSave, saving }) {
  const [editing, setEditing] = React.useState(false);
  const [text, setText] = React.useState(value);
  const bgColor = color === 'emerald' ? 'bg-emerald-50/50 border-emerald-100/50' : 'bg-blue-50/50 border-blue-100/50';
  const numColor = color === 'emerald' ? 'text-emerald-700' : 'text-blue-700';

  React.useEffect(() => { setText(value); }, [value]);

  if (isFinalized || !editing) {
    return (
      <div
        className={`flex items-start gap-2 p-2.5 rounded-lg mb-2 border cursor-pointer group ${bgColor} hover:shadow-sm transition-shadow`}
        onClick={() => !isFinalized && setEditing(true)}
        title={isFinalized ? '' : 'Click to edit'}
      >
        <span className={`text-xs font-bold mt-0.5 ${numColor}`}>{index + 1}</span>
        <p className="text-sm flex-1 leading-relaxed">{text}</p>
        {!isFinalized && (
          <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 mt-0.5 flex-shrink-0">edit</span>
        )}
      </div>
    );
  }

  return (
    <div className={`p-2.5 rounded-lg mb-2 border ${bgColor}`}>
      <div className="flex items-start gap-2">
        <span className={`text-xs font-bold mt-2 ${numColor}`}>{index + 1}</span>
        <textarea
          className="flex-1 text-sm bg-white border rounded-lg p-2 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
      </div>
      <div className="flex justify-end gap-1.5 mt-2">
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { setText(value); setEditing(false); }}>Cancel</Button>
        <Button size="sm" className="h-6 text-xs" style={{ background: '#1f2a4d' }} disabled={saving}
          onClick={() => { onSave(text); setEditing(false); }}>
          {saving ? <Loader2 size={10} className="mr-1 animate-spin" /> : <Save size={10} className="mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}

export default function ReportBuilderPage({ reportId, clientId, navigate }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [activeSection, setActiveSection] = useState('executive_summary');
  const [shareDialog, setShareDialog] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [creatingLink, setCreatingLink] = useState(false);
  const [editAlertOpen, setEditAlertOpen] = useState(false);
  const [unfinalizing, setUnfinalizing] = useState(false);

  const fetchReport = async () => {
    try {
      const data = await api(`/api/reports/${reportId}`);
      setReport(data);
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (reportId) fetchReport(); }, [reportId]);

  const saveSection = async (sectionKey, content) => {
    setSavingSection(sectionKey);
    try {
      await api(`/api/reports/${reportId}/sections`, {
        method: 'PUT',
        body: JSON.stringify({ section_key: sectionKey, content: typeof content === 'string' ? content : JSON.stringify(content) })
      });
      toast.success('Section saved');
      fetchReport();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSection(null);
    }
  };

  const regenerateSection = async (sectionKey) => {
    setRegenerating(sectionKey);
    try {
      const currentContent = report?.ai_data?.[sectionKey] || '';
      await api(`/api/reports/${reportId}/regenerate-section`, {
        method: 'POST',
        body: JSON.stringify({
          section_key: sectionKey,
          content: typeof currentContent === 'string' ? currentContent : JSON.stringify(currentContent)
        })
      });
      toast.success('Section regenerated with AI');
      fetchReport();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegenerating(null);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backendUrl}/api/reports/${reportId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report?.client_name || 'report'}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (err) {
      toast.error('PDF download failed: ' + err.message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await api(`/api/reports/${reportId}/finalize`, { method: 'PUT' });
      toast.success('Report finalized and saved!');
      fetchReport();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFinalizing(false);
    }
  };

  const handleUnfinalize = async () => {
    setUnfinalizing(true);
    try {
      await api(`/api/reports/${reportId}/unfinalize`, { method: 'PUT' });
      toast.success('Report reopened for editing');
      setEditAlertOpen(false);
      fetchReport();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUnfinalizing(false);
    }
  };

  const openShareDialog = async () => {
    setShareDialog(true);
    try {
      const links = await api(`/api/share-links?report_id=${reportId}`);
      setShareLinks(links);
    } catch (err) { setShareLinks([]); }
  };

  const createShareLink = async () => {
    setCreatingLink(true);
    try {
      const link = await api('/api/share-links', {
        method: 'POST',
        body: JSON.stringify({ report_id: reportId, expires_days: 7 })
      });
      toast.success('Share link created!');
      setShareLinks(prev => [link, ...prev]);
    } catch (err) { toast.error(err.message); }
    finally { setCreatingLink(false); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/shared/${token}`);
    toast.success('Link copied!');
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  const ai = report?.ai_data || {};
  const metrics = report?.metrics || {};
  const platforms = metrics?.platforms || {};
  const org = metrics?.organic_summary || {};
  const clientPlatforms = report?.branding?.platforms || Object.keys(platforms);
  const isFinalized = report?.status === 'finalized';

  // Build sections only for enabled platforms
  const activePlatformNames = clientPlatforms.filter(p => platforms[p]);

  const sections = [
    { key: 'executive_summary', label: 'Executive Summary', type: 'text', icon: FileText },
    { key: 'overview_metrics', label: 'Overview & Charts', type: 'metrics', icon: BarChart3 },
    { key: 'organic_highlights', label: 'Highlights', type: 'list', icon: Sparkles },
    { key: 'organic_opportunities', label: 'Opportunities', type: 'list', icon: Eye },
    ...activePlatformNames.map(p => ({
      key: `platform_${p}`,
      label: PLATFORM_META[p]?.label || p.charAt(0).toUpperCase() + p.slice(1),
      type: 'platform',
      icon: null, // use PlatformIcon instead
      platform: p,
    })),
    { key: 'next_phase', label: 'Next Phase', type: 'text', icon: FileText },
  ];

  const getSectionContent = (section) => {
    if (section.type === 'platform') {
      const pName = section.key.replace('platform_', '');
      const pData = ai.platform_summaries?.[pName] || {};
      return { highlights: pData.highlights || [], opportunities: pData.opportunities || [] };
    }
    if (section.type === 'metrics') return {};
    return ai[section.key] || (section.type === 'list' ? [] : '');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-[500px] w-full rounded-xl" />
      </div>
    );
  }

  if (!reportId) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground mb-4">No report selected</p>
        <Button variant="outline" onClick={() => navigate('reports', { clientId })}>Go to Reports</Button>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground mb-4">Report not found</p>
        <Button variant="outline" onClick={() => navigate('reports', { clientId })}>Go to Reports</Button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="report-builder-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('reports', { clientId })}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {report.client_name} Report
              </h1>
              <Badge variant={isFinalized ? 'default' : 'secondary'} className={isFinalized ? 'bg-emerald-600' : ''}>
                {isFinalized ? 'Finalized' : 'Draft'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {report.date_range?.start} to {report.date_range?.end}
              {report.comparison_range?.start && ` vs ${report.comparison_range.start} to ${report.comparison_range.end}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isFinalized ? (
            <Button size="sm" onClick={handleFinalize} disabled={finalizing} data-testid="finalize-report-button" style={{ background: '#16a34a' }}>
              {finalizing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle2 size={14} className="mr-2" />}
              Finalize
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditAlertOpen(true)} data-testid="edit-finalized-button"
              className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Pencil size={14} className="mr-2" /> Edit Report
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={openShareDialog} data-testid="share-report-button">
            <Share2 size={14} className="mr-2" /> Share
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloadingPdf} data-testid="pdf-export-button">
            {downloadingPdf ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
            Export PDF
          </Button>
        </div>
      </div>

      {/* Two pane layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left - Section outline */}
        <div className="col-span-3" data-testid="report-builder-outline">
          <Card className="sticky top-6">
            <CardContent className="p-4">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Report Sections</h3>
              <div className="space-y-0.5">
                {sections.map(section => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.key}
                      onClick={() => setActiveSection(section.key)}
                      className={`w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === section.key
                          ? 'bg-[#1f2a4d] text-white'
                          : 'hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {section.platform ? (
                        <PlatformIcon platform={section.platform} size={16} />
                      ) : (
                        Icon && <Icon size={14} />
                      )}
                      {section.label}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right - Editor canvas */}
        <div className="col-span-9">
          {sections.filter(s => s.key === activeSection).map(section => {
            const content = getSectionContent(section);

            return (
              <div key={section.key} className="space-y-4">
                {/* Section header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {section.platform && <PlatformIcon platform={section.platform} size={24} />}
                    <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {section.label}
                    </h2>
                  </div>
                  {section.type !== 'metrics' && !isFinalized && (
                    <Button variant="outline" size="sm" onClick={() => regenerateSection(section.key)} disabled={regenerating === section.key} data-testid="ai-generate-section-button">
                      {regenerating === section.key ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
                      Regenerate with AI
                    </Button>
                  )}
                </div>

                {/* Overview & Charts section */}
                {section.type === 'metrics' && metrics.data_source==='metricool' && <MetricoolOrganicPanel data={metrics.metricool} internal={false}/>}
                {section.type === 'metrics' && metrics.data_source!=='metricool' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <MetricCard label="Impressions" value={org.impressions?.current || 0} changePct={org.impressions?.change_pct || 0} />
                      <MetricCard label="Engagements" value={org.engagements?.current || 0} changePct={org.engagements?.change_pct || 0} />
                      <MetricCard label="Engagement Rate" value={org.engagement_rate?.current || 0} suffix="%" changePct={org.engagement_rate?.change_pct || 0} />
                      <MetricCard label="Follower Growth" value={org.follower_growth?.current || 0} prefix="+" changePct={org.follower_growth?.change_pct || 0} />
                    </div>
                    {metrics.date_labels && metrics.data_source!=='metricool' && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <ReportChart
                          title="Impressions by Platform"
                          data={Object.fromEntries(activePlatformNames.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => [p, platforms[p]?.daily_impressions || platforms[p]?.daily_views]))}
                          dataKeys={activePlatformNames.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => ({ key: p, name: p.charAt(0).toUpperCase() + p.slice(1) }))}
                          dateLabels={metrics.date_labels}
                        />
                        <ReportChart
                          title="Engagements by Platform"
                          data={Object.fromEntries(activePlatformNames.filter(p => platforms[p]?.daily_engagements).map(p => [p, platforms[p]?.daily_engagements]))}
                          dataKeys={activePlatformNames.filter(p => platforms[p]?.daily_engagements).map(p => ({ key: p, name: p.charAt(0).toUpperCase() + p.slice(1) }))}
                          dateLabels={metrics.date_labels}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Text sections (Executive Summary, Next Phase) */}
                {section.type === 'text' && (
                  <Card className="rounded-2xl shadow-sm">
                    <CardContent className="p-6 sm:p-8">
                      <RichTextEditor
                        content={typeof content === 'string' ? content : ''}
                        onSave={(newContent) => saveSection(section.key, newContent)}
                        saving={savingSection === section.key}
                        minimal={isFinalized}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Highlights / Opportunities - with chart context above */}
                {section.type === 'list' && (
                  <div className="space-y-4">
                    {/* Context chart so user can verify text against data */}
                    {metrics.date_labels && metrics.data_source!=='metricool' && (
                      <Card className="rounded-2xl border-dashed border-2 border-muted bg-muted/20">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <BarChart3 size={14} className="text-muted-foreground" />
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Data Reference</span>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
                            <div className="p-2.5 bg-white rounded-lg border text-center">
                              <div className="text-[10px] text-muted-foreground uppercase">Impressions</div>
                              <div className="text-lg font-semibold tabular-nums" style={{ color: '#1f2a4d' }}>{(org.impressions?.current || 0).toLocaleString()}</div>
                              <div className="text-[10px] text-emerald-600 font-medium">+{org.impressions?.change_pct || 0}%</div>
                            </div>
                            <div className="p-2.5 bg-white rounded-lg border text-center">
                              <div className="text-[10px] text-muted-foreground uppercase">Engagements</div>
                              <div className="text-lg font-semibold tabular-nums" style={{ color: '#1f2a4d' }}>{(org.engagements?.current || 0).toLocaleString()}</div>
                              <div className="text-[10px] text-emerald-600 font-medium">+{org.engagements?.change_pct || 0}%</div>
                            </div>
                            <div className="p-2.5 bg-white rounded-lg border text-center">
                              <div className="text-[10px] text-muted-foreground uppercase">Engagement Rate</div>
                              <div className="text-lg font-semibold tabular-nums" style={{ color: '#1f2a4d' }}>{org.engagement_rate?.current || 0}%</div>
                              <div className="text-[10px] text-emerald-600 font-medium">+{org.engagement_rate?.change_pct || 0}%</div>
                            </div>
                            <div className="p-2.5 bg-white rounded-lg border text-center">
                              <div className="text-[10px] text-muted-foreground uppercase">Follower Growth</div>
                              <div className="text-lg font-semibold tabular-nums" style={{ color: '#1f2a4d' }}>+{org.follower_growth?.current || 0}</div>
                            </div>
                          </div>
                          <ReportChart
                            title="Performance Trend"
                            data={Object.fromEntries(activePlatformNames.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).slice(0, 3).map(p => [p, platforms[p]?.daily_impressions || platforms[p]?.daily_views]))}
                            dataKeys={activePlatformNames.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).slice(0, 3).map(p => ({ key: p, name: p.charAt(0).toUpperCase() + p.slice(1) }))}
                            dateLabels={metrics.date_labels}
                          />
                        </CardContent>
                      </Card>
                    )}

                    {/* Editable list */}
                    <Card className="rounded-2xl shadow-sm">
                      <CardContent className="p-6 sm:p-8">
                        <div className="space-y-3">
                          {(Array.isArray(content) ? content : []).map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-xl border">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1f2a4d] text-white text-xs flex items-center justify-center font-semibold mt-0.5">
                                {idx + 1}
                              </span>
                              {isFinalized ? (
                                <p className="text-sm flex-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: item }} />
                              ) : (
                                <RichTextEditor
                                  content={item}
                                  onSave={(newContent) => {
                                    const updated = [...content];
                                    updated[idx] = newContent;
                                    saveSection(section.key, JSON.stringify(updated));
                                  }}
                                  saving={savingSection === section.key}
                                  minimal
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Platform sections - chart + text side by side */}
                {section.type === 'platform' && (() => {
                  const pName = section.key.replace('platform_', '');
                  const pData = platforms[pName] || {};
                  return (
                    <div className="space-y-4">
                      {/* Metrics row */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {pData.impressions && <MetricCard label="Impressions" value={pData.impressions.current} changePct={pData.impressions.change_pct} />}
                        {pData.engagements && <MetricCard label="Engagements" value={pData.engagements.current} changePct={pData.engagements.change_pct} />}
                        {pData.engagement_rate && <MetricCard label="ER" value={pData.engagement_rate.current} suffix="%" changePct={pData.engagement_rate.change_pct} />}
                        {pData.followers && <MetricCard label="Followers" value={pData.followers.current} changePct={pData.followers.change_pct} />}
                        {pData.views && <MetricCard label="Views" value={pData.views.current} changePct={pData.views.change_pct} />}
                        {pData.watch_time_hours && <MetricCard label="Watch Time" value={pData.watch_time_hours.current} suffix="h" changePct={pData.watch_time_hours.change_pct} />}
                        {pData.subscribers && <MetricCard label="Subscribers" value={pData.subscribers.current} changePct={pData.subscribers.change_pct} />}
                      </div>

                      {/* Chart + Text side by side */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Left: Chart */}
                        <div>
                          {pData.date_labels && (pData.daily_impressions || pData.daily_views) && (
                            <ReportChart
                              title={`${section.label} Performance`}
                              data={{
                                main: pData.daily_impressions || pData.daily_views,
                                ...(pData.daily_engagements ? { eng: pData.daily_engagements } : {})
                              }}
                              dataKeys={[
                                { key: 'main', name: pData.daily_impressions ? 'Impressions' : 'Views' },
                                ...(pData.daily_engagements ? [{ key: 'eng', name: 'Engagements' }] : [])
                              ]}
                              dateLabels={pData.date_labels}
                            />
                          )}
                          {pData.daily_clicks && pData.date_labels && (
                            <div className="mt-4">
                              <ReportChart
                                title={`${section.label} Clicks`}
                                data={{ clicks: pData.daily_clicks }}
                                dataKeys={[{ key: 'clicks', name: 'Clicks' }]}
                                dateLabels={pData.date_labels}
                              />
                            </div>
                          )}
                        </div>

                        {/* Right: Highlights & Opportunities — Editable */}
                        <Card className="rounded-2xl shadow-sm h-fit">
                          <CardContent className="p-5">
                            <div className="space-y-5">
                              <div>
                                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> Highlights
                                </h4>
                                {(content.highlights || []).length === 0 ? (
                                  <p className="text-xs text-muted-foreground italic">No highlights generated</p>
                                ) : (
                                  (content.highlights || []).map((item, idx) => (
                                    <EditableListItem
                                      key={`${pName}-hl-${idx}`}
                                      value={stripHtml(item)}
                                      index={idx}
                                      color="emerald"
                                      isFinalized={isFinalized}
                                      onSave={(newVal) => {
                                        const updated = [...(content.highlights || [])];
                                        updated[idx] = newVal;
                                        saveSection(`platform_${pName}_highlights`, JSON.stringify(updated));
                                      }}
                                      saving={savingSection === `platform_${pName}_highlights`}
                                    />
                                  ))
                                )}
                              </div>
                              <Separator />
                              <div>
                                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-blue-500" /> Opportunities
                                </h4>
                                {(content.opportunities || []).length === 0 ? (
                                  <p className="text-xs text-muted-foreground italic">No opportunities generated</p>
                                ) : (
                                  (content.opportunities || []).map((item, idx) => (
                                    <EditableListItem
                                      key={`${pName}-op-${idx}`}
                                      value={stripHtml(item)}
                                      index={idx}
                                      color="blue"
                                      isFinalized={isFinalized}
                                      onSave={(newVal) => {
                                        const updated = [...(content.opportunities || [])];
                                        updated[idx] = newVal;
                                        saveSection(`platform_${pName}_opportunities`, JSON.stringify(updated));
                                      }}
                                      saving={savingSection === `platform_${pName}_opportunities`}
                                    />
                                  ))
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Top 5 Posts */}
                      {pData.top_posts && pData.top_posts.length > 0 && (
                        <Card className="rounded-2xl shadow-sm">
                          <CardContent className="p-5">
                            <h4 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                              <BarChart3 size={15} /> Top {pData.top_posts.length} Posts by Engagement
                            </h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-8">#</TableHead>
                                  <TableHead>Post</TableHead>
                                  <TableHead className="text-center">Type</TableHead>
                                  <TableHead className="text-right">Impressions</TableHead>
                                  <TableHead className="text-right">Engagements</TableHead>
                                  <TableHead className="text-right">ER</TableHead>
                                  <TableHead className="text-right">Likes</TableHead>
                                  <TableHead className="text-right">Comments</TableHead>
                                  <TableHead className="text-right">Shares</TableHead>
                                  {pName === 'instagram' && <TableHead className="text-right">Saves</TableHead>}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pData.top_posts.map((post, idx) => (
                                  <TableRow key={idx} className="hover:bg-muted/30">
                                    <TableCell>
                                      <span className="w-5 h-5 rounded-full bg-[#1f2a4d] text-white text-[10px] flex items-center justify-center font-bold">
                                        {post.rank}
                                      </span>
                                    </TableCell>
                                    <TableCell>
                                      {post.perma_link ? (
                                        <a href={post.perma_link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:text-[#e01e25] hover:underline transition-colors">{post.title}</a>
                                      ) : (
                                        <div className="text-sm font-medium">{post.title}</div>
                                      )}
                                      <div className="text-xs text-muted-foreground">{post.date}</div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Badge variant="secondary" className="text-[10px]">{post.type}</Badge>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums font-medium">{post.impressions?.toLocaleString()}</TableCell>
                                    <TableCell className="text-right tabular-nums font-semibold" style={{ color: '#1f2a4d' }}>{post.engagements?.toLocaleString()}</TableCell>
                                    <TableCell className="text-right tabular-nums">{post.engagement_rate}%</TableCell>
                                    <TableCell className="text-right tabular-nums">{post.likes?.toLocaleString()}</TableCell>
                                    <TableCell className="text-right tabular-nums">{post.comments?.toLocaleString()}</TableCell>
                                    <TableCell className="text-right tabular-nums">{post.shares?.toLocaleString()}</TableCell>
                                    {pName === 'instagram' && <TableCell className="text-right tabular-nums">{post.saves?.toLocaleString()}</TableCell>}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="max-w-md" data-testid="share-link-dialog">
          <DialogHeader>
            <DialogTitle>Share Report</DialogTitle>
            <p className="text-sm text-muted-foreground">Generate a public link for client access</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button onClick={createShareLink} disabled={creatingLink} className="w-full" data-testid="share-report-button" style={{ background: '#1f2a4d' }}>
              {creatingLink ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Link2 size={14} className="mr-2" />}
              Generate New Share Link (7 days)
            </Button>
            {shareLinks.length > 0 && (
              <div className="space-y-3">
                <Separator />
                <h4 className="text-sm font-medium">Active Links</h4>
                {shareLinks.map((link, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input readOnly value={`${window.location.origin}/shared/${link.token}`} className="text-xs flex-1" data-testid="share-link-input" />
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => copyLink(link.token)} data-testid="share-link-copy-button">
                      <Copy size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Finalized Report Alert */}
      <Dialog open={editAlertOpen} onOpenChange={setEditAlertOpen}>
        <DialogContent className="max-w-sm" data-testid="edit-finalized-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-500" />
              Edit Finalized Report
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              This report has been finalized. Reopening it will set it back to <strong>Draft</strong> status. 
              You'll need to finalize it again after making changes.
            </p>
          </DialogHeader>
          <div className="py-3">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-800">
                Any existing share links will continue to show the current version until you finalize again.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAlertOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUnfinalize}
              disabled={unfinalizing}
              data-testid="confirm-edit-finalized"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {unfinalizing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Pencil size={14} className="mr-2" />}
              Reopen for Editing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
